import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {Caption, HeadingSmall} from "@khanacademy/wonder-blocks-typography";
import {useMutation} from "@repo/data-gql";
import {doNotTranslateYet} from "@repo/i18n";
import {Errors, KAError} from "@repo/logging";

import {SET_BASIC_SETTINGS_MUTATION} from "./basic-settings-mutation.ts";
import LanguageSelector from "./language-selector.tsx";
import {type BasicSettingsData} from "./types/account-settings-types.ts";

interface Props {
    initialUserLanguage: BasicSettingsData["userLanguage"];
    kaid: string;
    successfullyChangedSettings: () => unknown;
}

const LanguageSettings: React.FC<Props> = ({
    initialUserLanguage,
    kaid,
    successfullyChangedSettings,
}) => {
    const [userLanguage, setUserLanguage] = React.useState(initialUserLanguage);
    const [setBasicSettingsMutation, result] = useMutation(
        SET_BASIC_SETTINGS_MUTATION,
    );

    const storeSettingsAndModifyState = async () => {
        try {
            await setBasicSettingsMutation({userLanguage, kaid});
        } catch (e) {
            throw new KAError(
                "Failed to set basic settings for language",
                Errors.Internal,
                {
                    cause: e as Error,
                },
            );
        }

        successfullyChangedSettings();
    };

    return (
        <View
            testId="language-settings"
            style={{gap: sizing.size_160}}
            role="region"
            aria-label={doNotTranslateYet`Language settings`}
        >
            <HeadingSmall tag="h2">{doNotTranslateYet`Language settings`}</HeadingSmall>
            <LanguageSelector
                userLanguage={userLanguage || "unspecified"}
                onChange={(userLanguage) => setUserLanguage(userLanguage)}
                disabled={result?.status === "loading"}
            />
            <View style={styles.buttonContainer}>
                {result?.status === "error" && (
                    <Caption style={styles.error}>
                        {t`Oops. Something went wrong. Please try again.`}
                    </Caption>
                )}
                <Button
                    onClick={storeSettingsAndModifyState}
                    style={styles.button}
                    spinner={result?.status === "loading"}
                    disabled={initialUserLanguage === userLanguage}
                    testId="basic-settings-save-button"
                >
                    {t`Save changes`}
                </Button>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    button: {
        alignSelf: "flex-start",
        paddingInlineStart: sizing.size_160,
        paddingInlineEnd: sizing.size_160,
    },
    buttonContainer: {
        flexDirection: "row",
    },
    error: {
        color: semanticColor.status.critical.foreground,
        marginInlineStart: sizing.size_160,
    },
});

export default React.memo(LanguageSettings);
